import { Controller } from "stimulus";

// Content Studio editor controller.
//
// Responsibilities:
//   1. Live preview — syncs title, description, category, cover image and Trix body
//      to the right-hand preview panel as the user types.
//   2. Module picker dialog — three-phase flow:
//        idle    → left: module list,  right: idle hint
//        preview → left: module list,  right: module preview (placeholder data)
//        config  → left: config form,  right: live-updating preview
//      In config mode the grid switches to 50/50; a back button appears top-left.
//   3. Live module preview — config field input events fetch a fresh server render
//      and update the right-hand preview in real time.
//   4. Module insertion — POSTs to /content-studio/modules, inserts SGID into Trix.
//   5. Module editing — a click listener (capture phase, beats Trix) on the document
//      intercepts clicks on .cs-module-edit-btn elements that live inside Trix
//      attachment content. Opens the dialog pre-filled with the existing config.
//      Saving PATCHes /content-studio/modules/:id and calls
//      attachment.setAttributes({ content }) to update the Trix display in-place.
//
export default class extends Controller {
  static get targets() {
    return [
      // Live preview
      "titleInput",
      "titlePreview",
      "descriptionInput",
      "descriptionPreview",
      "categorySelect",
      "categoryPreview",
      "coverFileInput",
      "coverUrlInput",
      "uploadZoneImg",
      "uploadZonePlaceholder",
      "previewCoverWrap",
      "previewCoverImg",
      "bodyPreview",
      "slugInput",
      // Dialog
      "dialog",
      "dialogBody",
      "moduleList",
      "backToListBtn",
      "moduleTypeBtn",
      "modulePreview",
      "moduleConfig",
      "idleHint",
      "footerHint",
      "goToConfigBtn",
      "insertBtn",
    ];
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connect() {
    this.currentType = null;
    this.phase = "idle";

    // URLs
    this.modulesUrl         = this.data.get("modules-url");
    this.previewUrl         = this.data.get("preview-url");
    this.uploadUrl          = this.data.get("upload-url");
    this.deleteUploadUrl    = this.data.get("delete-upload-url");
    this.updateModuleUrlTpl = this.data.get("update-module-url"); // contains "MODULE_ID"

    this.csrfToken = (document.querySelector('meta[name="csrf-token"]') || {}).content || "";

    // Slug auto-fill state (new articles only)
    this.slugAutoFilled = false;

    // Edit mode state — set when the creative clicks ✎ Edit on a module
    this.editingModuleId   = null;
    this.editingModuleSgid = null;

    // Upload tracking — maps fieldId → signed_id for blobs uploaded this session.
    // Signed IDs are removed once the module is successfully inserted/updated so
    // they are only purged when the dialog closes without saving.
    this._pendingUploads = {};

    // Object URLs created by URL.createObjectURL — revoked on dialog close to
    // prevent memory leaks.
    this._objectUrls = {};

    // Set to true immediately before a successful insert/update so onDialogClose
    // knows not to purge the uploaded blobs.
    this._insertSucceeded = false;

    // Capture-phase listener so we intercept edit-button clicks before Trix does
    this._onDocumentClick = this._handleDocumentClick.bind(this);
    document.addEventListener("click", this._onDocumentClick, true);
  }

  disconnect() {
    document.removeEventListener("click", this._onDocumentClick, true);
    // Revoke any lingering object URLs
    Object.values(this._objectUrls || {}).forEach((u) => URL.revokeObjectURL(u));
  }

  // ── Live preview ─────────────────────────────────────────────────────────

  updateTitlePreview() {
    const val = this.titleInputTarget.value.trim();
    this.titlePreviewTarget.textContent =
      val || "Your article title will appear here…";
  }

  handleTitleBlur() {
    if (this.slugAutoFilled) return;
    if (!this.hasSlugInputTarget) return;
    const title = this.titleInputTarget.value.trim();
    if (!title) return;
    if (this.slugInputTarget.value.trim()) return;
    this.slugInputTarget.value = this.titleToSlug(title);
    this.slugAutoFilled = true;
  }

  titleToSlug(title) {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  updateDescriptionPreview() {
    this.descriptionPreviewTarget.textContent =
      this.descriptionInputTarget.value.trim();
  }

  updateCategoryPreview() {
    const val = this.categorySelectTarget.value;
    this.categoryPreviewTarget.textContent = val ? val.toUpperCase() : "";
    this.categoryPreviewTarget.hidden = !val;
  }

  updateCoverFromUrl() {
    const url = this.coverUrlInputTarget.value.trim();
    if (!url) return;
    this.previewCoverImgTarget.src = url;
    this.previewCoverWrapTarget.hidden = false;
  }

  triggerCoverUpload(event) {
    if (event.target !== this.coverFileInputTarget) {
      this.coverFileInputTarget.click();
    }
  }

  handleCoverFileChange() {
    const file = this.coverFileInputTarget.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      this.uploadZoneImgTarget.src = dataUrl;
      this.uploadZoneImgTarget.hidden = false;
      this.uploadZonePlaceholderTarget.hidden = true;
      this.previewCoverImgTarget.src = dataUrl;
      this.previewCoverWrapTarget.hidden = false;
    };
    reader.readAsDataURL(file);
  }

  syncBodyPreview(event) {
    const trixEditor = event.target;
    if (trixEditor && trixEditor.tagName === "TRIX-EDITOR") {
      this.bodyPreviewTarget.innerHTML = trixEditor.innerHTML;
    }
  }

  // ── Dialog open / close ───────────────────────────────────────────────────

  openDialog() {
    this._clearEditingState();
    this.setPhase("idle");
    this.dialogTarget.showModal();
  }

  closeDialog() {
    this.dialogTarget.close();
  }

  onDialogClose() {
    // If the user closed without a successful insert/update, purge any images
    // that were uploaded during this session so they don't orphan in storage.
    if (!this._insertSucceeded) {
      Object.entries(this._pendingUploads).forEach(([, signedId]) => {
        this._purgeBlob(signedId);
      });
    }
    this._pendingUploads  = {};
    this._insertSucceeded = false;

    // Revoke object URLs (local file previews) to release browser memory
    Object.values(this._objectUrls).forEach((u) => URL.revokeObjectURL(u));
    this._objectUrls = {};

    this.currentType = null;
    this._clearEditingState();
    this.moduleTypeBtnTargets.forEach((btn) =>
      btn.classList.remove("cs-module-type-btn--active"),
    );
    this.setPhase("idle");
  }

  backdropClick(event) {
    if (event.target === this.dialogTarget) {
      this.dialogTarget.close();
    }
  }

  // ── Module selection ──────────────────────────────────────────────────────

  selectModule(event) {
    const type = event.currentTarget.dataset.moduleType;
    this.currentType = type;
    this.moduleTypeBtnTargets.forEach((btn) =>
      btn.classList.toggle(
        "cs-module-type-btn--active",
        btn.dataset.moduleType === type,
      ),
    );
    this.setPhase("preview");
  }

  goToConfig() {
    if (!this.currentType) return;
    this.setPhase("config");
  }

  backToList() {
    this.currentType = null;
    this._clearEditingState();
    this.moduleTypeBtnTargets.forEach((btn) =>
      btn.classList.remove("cs-module-type-btn--active"),
    );
    this.setPhase("idle");
  }

  // ── Phase management ──────────────────────────────────────────────────────

  setPhase(phase) {
    this.phase = phase;

    if (this.hasDialogBodyTarget) {
      this.dialogBodyTarget.classList.toggle(
        "cs-module-dialog__body--config",
        phase === "config",
      );
    }

    if (this.hasModuleListTarget) {
      this.moduleListTarget.hidden = phase === "config";
    }
    if (this.hasBackToListBtnTarget) {
      this.backToListBtnTarget.hidden = phase !== "config";
    }

    this.moduleConfigTargets.forEach((el) => {
      el.hidden =
        !(phase === "config" && el.dataset.moduleType === this.currentType);
    });

    this.idleHintTarget.hidden = phase !== "idle";

    this.modulePreviewTargets.forEach((el) => {
      el.hidden = !(
        (phase === "preview" || phase === "config") &&
        el.dataset.moduleType === this.currentType
      );
    });

    const hints = {
      idle: "Choose a module from the left to preview it.",
      preview: "Looking good? Click Configure → to fill in your content.",
      config: "Fill in the fields — the preview updates as you type.",
    };
    this.footerHintTarget.textContent = hints[phase] || "";
    this.goToConfigBtnTarget.hidden = phase !== "preview";
    this.insertBtnTarget.disabled = phase !== "config";
  }

  // ── Module image upload ───────────────────────────────────────────────────

  handleModuleImageUpload(event) {
    const fileInput = event.target;
    const file = fileInput.files[0];
    if (!file || !this.uploadUrl) return;

    const fieldId   = fileInput.dataset.uploadFor;
    const urlInput  = document.getElementById(fieldId);
    const statusEl  = document.querySelector(`[data-upload-status-for="${fieldId}"]`);
    const previewEl = document.querySelector(`[data-upload-preview-for="${fieldId}"]`);

    if (!urlInput) return;

    // ── 1. Instant local preview ─────────────────────────────────────────
    // URL.createObjectURL is synchronous and always works — no server needed.
    // Revoke the previous object URL for this field to avoid memory leaks.
    if (this._objectUrls[fieldId]) {
      URL.revokeObjectURL(this._objectUrls[fieldId]);
    }
    const objectUrl = URL.createObjectURL(file);
    this._objectUrls[fieldId] = objectUrl;

    if (previewEl) {
      previewEl.src    = objectUrl;
      previewEl.hidden = false;
    }
    if (statusEl) statusEl.textContent = "Uploading…";

    // Clear the hidden URL input while upload is in flight so insert() doesn't
    // pick up a stale URL from a previous upload of this field.
    urlInput.value = "";

    // If a previous upload for this field is pending, purge it — the user chose
    // a different image before inserting.
    if (this._pendingUploads[fieldId]) {
      this._purgeBlob(this._pendingUploads[fieldId]);
      delete this._pendingUploads[fieldId];
    }

    // ── 2. Background upload to Active Storage ───────────────────────────
    const formData = new FormData();
    formData.append("file", file);

    fetch(this.uploadUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": this.csrfToken },
      body: formData,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (!data.url || !data.signed_id) throw new Error("Invalid upload response");

        // Persist the server URL in the hidden input so insert() stores it in config
        urlInput.value = data.url;

        // Track for cleanup if the dialog is closed without inserting
        this._pendingUploads[fieldId] = data.signed_id;

        if (statusEl) statusEl.textContent = file.name;

        // The config-form preview is already showing the local object URL, which
        // is fine for now. Trigger a live preview so the right panel also updates.
        if (this.phase === "config") this.updateLivePreview();
      })
      .catch(() => {
        urlInput.value = "";
        if (statusEl) statusEl.textContent = "Upload failed — please try again.";
        // Keep the local preview visible so the user can see their chosen image;
        // they can retry by picking the same file again.
      });
  }

  // Fire-and-forget DELETE to purge an orphaned blob from Active Storage.
  _purgeBlob(signedId) {
    if (!this.deleteUploadUrl || !signedId) return;
    fetch(`${this.deleteUploadUrl}?signed_id=${encodeURIComponent(signedId)}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: { "X-CSRF-Token": this.csrfToken },
    }).catch(() => {}); // silent — best-effort cleanup
  }

  // ── Live module preview ───────────────────────────────────────────────────

  onConfigInput() {
    if (this.phase !== "config" || !this.currentType) return;
    clearTimeout(this._livePreviewTimer);
    this._livePreviewTimer = setTimeout(() => this.updateLivePreview(), 350);
  }

  updateLivePreview() {
    if (!this.currentType || !this.previewUrl) return;

    const panel = this.moduleConfigTargets.find(
      (el) => el.dataset.moduleType === this.currentType,
    );
    if (!panel) return;

    const params = new URLSearchParams();
    panel.querySelectorAll("[data-field-name]").forEach((el) => {
      const name = el.dataset.fieldName;
      const type = el.dataset.fieldType;

      // For hidden URL inputs (file-upload fields) prefer the local object URL
      // created by URL.createObjectURL — blob: URLs are valid as <img src> within
      // the same browser context and work even before the server upload completes.
      // Fall back to the stored server URL once the upload has finished.
      let val;
      if (el.type === "hidden" && this._objectUrls[el.id]) {
        val = this._objectUrls[el.id];
      } else {
        val = el.value.trim();
      }
      if (!val) return;

      if (type === "array") {
        val
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean)
          .forEach((v) => params.append(`config[${name}][]`, v));
      } else {
        params.set(`config[${name}]`, val);
      }
    });

    fetch(`${this.previewUrl}/${this.currentType}?${params.toString()}`, {
      credentials: "same-origin",
      headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data || !data.html) return;
        const previewEl = this.modulePreviewTargets.find(
          (el) => el.dataset.moduleType === this.currentType,
        );
        if (!previewEl) return;
        const inner = previewEl.querySelector(".cs-module-preview-wrapper__inner");
        if (inner) inner.innerHTML = data.html;
      })
      .catch(() => {});
  }

  // ── Module editing (click ✎ Edit inside Trix content) ────────────────────

  // Capture-phase document listener — fires before Trix processes the click so
  // we can prevent Trix from selecting the attachment instead of opening edit.
  _handleDocumentClick(event) {
    const btn = event.target.closest(".cs-module-edit-btn");
    if (!btn) return;
    event.preventDefault();
    event.stopPropagation();
    this._startEditModule(btn);
  }

  // Opens the module picker dialog in config phase, pre-populated with the
  // existing configuration extracted from the button's data attributes.
  _startEditModule(btn) {
    const moduleId   = btn.closest("[data-module-id]").dataset.moduleId;
    const moduleSgid = btn.closest("[data-module-sgid]").dataset.moduleSgid;
    const moduleType = btn.closest("[data-module-type]").dataset.moduleType;
    let config = {};
    try {
      config = JSON.parse(
        btn.closest("[data-module-config]").dataset.moduleConfig || "{}",
      );
    } catch (_) {}

    // Store edit context
    this.editingModuleId   = moduleId;
    this.editingModuleSgid = moduleSgid;
    this.currentType       = moduleType;

    // Highlight the active module type button in the list
    this.moduleTypeBtnTargets.forEach((b) =>
      b.classList.toggle(
        "cs-module-type-btn--active",
        b.dataset.moduleType === moduleType,
      ),
    );

    // Pre-fill all config form fields with existing values
    const panel = this.moduleConfigTargets.find(
      (el) => el.dataset.moduleType === moduleType,
    );
    if (panel) {
      panel.querySelectorAll("[data-field-name]").forEach((el) => {
        const name = el.dataset.fieldName;
        const val  = config[name];
        if (val === undefined || val === null) return;

        if (el.type === "hidden") {
          // File-upload URL store — also restore thumbnail and status
          el.value = val;
          const fieldId   = el.id;
          const previewEl = panel.querySelector(`[data-upload-preview-for="${fieldId}"]`);
          const statusEl  = panel.querySelector(`[data-upload-status-for="${fieldId}"]`);
          if (previewEl && val) { previewEl.src = val; previewEl.hidden = false; }
          if (statusEl) statusEl.textContent = "Previously uploaded image";
        } else {
          el.value = Array.isArray(val) ? val.join(", ") : String(val);
        }
      });
    }

    // Open dialog directly at config phase; change button label to "Update"
    this.setPhase("config");
    this.insertBtnTarget.textContent = "Update";
    this.dialogTarget.showModal();
  }

  _clearEditingState() {
    this.editingModuleId   = null;
    this.editingModuleSgid = null;
    this.insertBtnTarget.textContent = "Insert";
  }

  // ── Insert / Update ───────────────────────────────────────────────────────

  insert() {
    if (this.editingModuleId) {
      this._performUpdate();
    } else {
      this._performInsert();
    }
  }

  // Shared: validate required fields and collect config from the active panel.
  // Returns the config object on success, or null if validation failed.
  _collectConfig() {
    const panel = this.moduleConfigTargets.find(
      (el) => el.dataset.moduleType === this.currentType,
    );
    if (!panel) return null;

    let valid = true;
    panel.querySelectorAll("[required]").forEach((el) => {
      if (!el.value.trim()) {
        el.classList.add("cs__form-input--error");
        valid = false;
      } else {
        el.classList.remove("cs__form-input--error");
      }
    });
    if (!valid) {
      this.footerHintTarget.textContent =
        "Please fill in the required fields marked with *.";
      return null;
    }

    const config = {};
    panel.querySelectorAll("[data-field-name]").forEach((el) => {
      const name = el.dataset.fieldName;
      const type = el.dataset.fieldType;
      const val  = el.value.trim();
      if (type === "array") {
        config[name] = val
          ? val.split(",").map((v) => v.trim()).filter(Boolean)
          : [];
      } else if (type === "number") {
        config[name] = val ? Number(val) : null;
      } else {
        config[name] = val || null;
      }
    });

    return config;
  }

  _performInsert() {
    if (!this.currentType) return;
    const config = this._collectConfig();
    if (!config) return;

    this.insertBtnTarget.disabled = true;
    this.insertBtnTarget.textContent = "Inserting…";

    fetch(this.modulesUrl, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ module_type: this.currentType, configuration: config }),
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        const trix = document.querySelector("trix-editor");
        if (trix && trix.editor && data.sgid) {
          const attachment = new window.Trix.Attachment({
            sgid: data.sgid,
            content: data.content,
          });
          trix.editor.insertAttachment(attachment);
        }
        // Mark success so onDialogClose does NOT purge the uploaded images
        this._insertSucceeded = true;
        this._pendingUploads  = {};
        this.resetConfigForms();
        this.closeDialog();
      })
      .catch(() => alert("Failed to insert module. Please try again."))
      .finally(() => {
        this.insertBtnTarget.disabled = false;
        this.insertBtnTarget.textContent = "Insert";
      });
  }

  _performUpdate() {
    if (!this.currentType || !this.editingModuleId) return;
    const config = this._collectConfig();
    if (!config) return;

    this.insertBtnTarget.disabled = true;
    this.insertBtnTarget.textContent = "Saving…";

    const url = this.updateModuleUrlTpl.replace("MODULE_ID", this.editingModuleId);

    fetch(url, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "X-CSRF-Token": this.csrfToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ configuration: config }),
    })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((data) => {
        // Update the Trix attachment content in-place so the editor immediately
        // reflects the changes without the user having to save and reload.
        const trix = document.querySelector("trix-editor");
        if (trix && trix.editor && data.sgid) {
          const attachments = trix.editor.getDocument().getAttachments();
          const attachment  = attachments.find(
            (a) => a.getAttributes().sgid === this.editingModuleSgid,
          );
          if (attachment) {
            attachment.setAttributes({ content: data.content });
          }
        }
        // Mark success so onDialogClose does NOT purge the uploaded images
        this._insertSucceeded = true;
        this._pendingUploads  = {};
        this.resetConfigForms();
        this.closeDialog();
      })
      .catch(() => alert("Failed to update module. Please try again."))
      .finally(() => {
        this.insertBtnTarget.disabled = false;
        this.insertBtnTarget.textContent = "Update";
      });
  }

  // ── Form reset ────────────────────────────────────────────────────────────

  resetConfigForms() {
    this.moduleConfigTargets.forEach((panel) => {
      panel.querySelectorAll("input, textarea, select").forEach((el) => {
        el.value = "";
        el.classList.remove("cs__form-input--error");
      });
      panel.querySelectorAll("[data-upload-preview-for]").forEach((img) => {
        img.hidden = true;
        img.src    = "";
      });
      panel.querySelectorAll("[data-upload-status-for]").forEach((el) => {
        el.textContent = "";
      });
    });

    // Revoke any object URLs created for local file previews
    Object.values(this._objectUrls).forEach((u) => URL.revokeObjectURL(u));
    this._objectUrls = {};
  }
}
